import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/posthog';
import { upsellEvents } from '@/lib/analytics/events/upsell';
import { useLocalizedNavigate } from '@/components/i18n';
import { getUpgradeUrl } from '@/lib/upgrade';

const MILESTONE_STORAGE_PREFIX = 'milestone-nudge-';
const AUTO_DISMISS_MS = 10_000;

interface MilestoneConfig {
  title: string;
  description: string;
  ctaText: string;
}

const MILESTONE_CONFIGS: Record<string, MilestoneConfig> = {
  first_strategy: {
    title: 'Your strategy is ready!',
    description:
      'Upgrade to unlock full negotiation scripts + 25 regenerations',
    ctaText: 'Unlock more',
  },
  five_documents: {
    title: '5 documents uploaded!',
    description: 'Upgrade for 50 uploads + AI Case Intelligence',
    ctaText: 'Get more uploads',
  },
  first_support_calc: {
    title: 'Support calculation complete!',
    description: 'Upgrade for advanced scenarios and export',
    ctaText: 'Explore scenarios',
  },
  onboarding_complete: {
    title: 'Setup complete!',
    description: 'Start your 7-day free trial to unlock everything',
    ctaText: 'Start free trial',
  },
};

function isAlreadyShown(milestoneId: string): boolean {
  try {
    return localStorage.getItem(`${MILESTONE_STORAGE_PREFIX}${milestoneId}-shown`) === 'true';
  } catch {
    return false;
  }
}

function markShown(milestoneId: string): void {
  try {
    localStorage.setItem(`${MILESTONE_STORAGE_PREFIX}${milestoneId}-shown`, 'true');
  } catch {
    // localStorage unavailable
  }
}

interface MilestoneToastContentProps {
  milestone: string;
  config: MilestoneConfig;
  toastId: string | number;
  onCtaClick: () => void;
}

function MilestoneToastContent({
  config,
  onCtaClick,
}: MilestoneToastContentProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
        <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400" />
      </div>
      <div className="min-w-0 flex-1">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {config.title}
        </h4>
        <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
          {config.description}
        </p>
        <Button
          size="sm"
          variant="accent"
          onClick={onCtaClick}
          className="mt-2"
        >
          {config.ctaText}
          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function showMilestoneToast(
  milestoneId: string,
  navigate: (path: string) => void,
): void {
  const config = MILESTONE_CONFIGS[milestoneId];
  if (!config) return;

  // Check if already shown
  if (isAlreadyShown(milestoneId)) return;

  // Mark as shown
  markShown(milestoneId);

  // Fire analytics
  const event = upsellEvents.milestoneNudgeShown(
    milestoneId,
    `milestone_${milestoneId}`,
  );
  trackEvent(event.event, event.properties);

  const toastId = `milestone-${milestoneId}`;

  const handleCtaClick = () => {
    const clickEvent = upsellEvents.milestoneNudgeClicked(
      milestoneId,
      `milestone_${milestoneId}`,
    );
    trackEvent(clickEvent.event, clickEvent.properties);
    toast.dismiss(toastId);
    navigate(getUpgradeUrl(`milestone_${milestoneId}`));
  };

  toast.custom(
    () => (
      <MilestoneToastContent
        milestone={milestoneId}
        config={config}
        toastId={toastId}
        onCtaClick={handleCtaClick}
      />
    ),
    {
      id: toastId,
      duration: AUTO_DISMISS_MS,
      className: 'border border-amber-200 dark:border-amber-800',
    },
  );
}

/**
 * Hook that provides a function to trigger milestone nudge toasts.
 * Each milestone is shown only once per user (stored in localStorage).
 */
export function useMilestoneNudge() {
  const navigate = useLocalizedNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const triggerMilestone = (milestoneId: string) => {
    showMilestoneToast(milestoneId, navigateRef.current);
  };

  return { triggerMilestone };
}
